
///
/// EarthBrowser
///
/// Created by Matt Giger on 11/20/2024
/// Copyright (c) 2024 Geodata Labs
///

import { TextureFrame, type TextureSeries } from "@/earthbrowser/datasets/TextureSeries"
import { RenderState } from "@stovokor/engine/RenderState"
import { type matrix } from "@stovokor/math/matrix"
import { type vec3 } from "@stovokor/math/vector"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { type Material } from "@stovokor/shader/Material"
import { Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"

export class RadarMaterial implements Material {
    public transparency = true
    public available = true
    public cameraPosition = new UniformDynamic<vec3>()
    public modelMatrix = new UniformDynamic<matrix>()
	// World -> box space, inverted once per draw here instead of per fragment
	public inverseModelMatrix = new UniformDynamic<matrix>()
    public lightDirection = new UniformDynamic<vec3>()
    public threshold = new UniformDynamic<number>()
    public density = new UniformDynamic<number>()
	// March step in data-grid texels along the ray's dominant axis
	// (1 = one sample per texel; larger is faster and coarser)
    public stepTexels = new UniformDynamic<number>()
    public maxSteps = new UniformDynamic<number>()
    public colorScale = new UniformDynamic<number>()
    public maxHeight = new UniformDynamic<number>()
	public volumeFade = new UniformDynamic<number>()
	public volumeBlend = new UniformDynamic<number>()
	public volumeTexture0 = new UniformDynamic<Texture>()
	public volumeTexture1 = new UniformDynamic<Texture>()

	public textureSeries: TextureSeries<RadarAtlasFrame>

	constructor(
		textureSeries: TextureSeries<RadarAtlasFrame>,
        threshold: number = -10,
        density: number = 5.0,
        stepTexels: number = 1.0,
        maxSteps: number = 200,
        maxHeight: number = 15,
    ) {
		this.textureSeries = textureSeries
        this.threshold.value = threshold
        this.density.value = density
        this.stepTexels.value = stepTexels
        this.maxSteps.value = maxSteps
        this.maxHeight.value = maxHeight
        this.colorScale.value = 0 // 0 = reflectivity, 1 = height, 2 = distance
    }

    public renderUpdate(state: RenderState): void {
        this.cameraPosition.value = state.camera.position
        this.modelMatrix.value = state.modelMatrix
        this.inverseModelMatrix.value = state.modelMatrix.invert()
        this.lightDirection.value = RenderState.lightDirection
		this.volumeFade.value = this.textureSeries.fadeFactor
		this.volumeBlend.value = this.textureSeries.blendFactor
		this.volumeTexture0.value = this.textureSeries.texture0
		this.volumeTexture1.value = this.textureSeries.texture1

    }
}

// Create packed 2D texture to store 3D volume
// 10x10 grid of z-layers to fit 96 layers
export class RadarAtlasFrame extends TextureFrame {

	override createNewTexture(): void {
        // Don't pre-allocate size - let ImageLoader determine it from the actual image
        // Use RGBA format to match the PNG image format from ImageLoader
        this.texture = new Texture(
            this.context,
            1,
            1,
            InternalFormat.RGBA8,
            PixelFormat.RGBA,
            ComponentType.UnsignedByte,
            TextureFilter.Linear,
            TextureFilter.Linear,
            TextureWrap.ClampToEdge,
            TextureWrap.ClampToEdge
        )
	}
}
